import { TableOutlined, UserAddOutlined, DownOutlined } from '@ant-design/icons';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Col,
  Dropdown,
  Input,
  message,
  Popover,
  Select,
  Space,
  Radio,
  Table,
} from 'antd';
import React, { useEffect, useState } from 'react';
import { useQueryClient } from 'react-query';
import { createSearchParams, Outlet, useSearchParams } from 'react-router-dom';
import countries from 'world_countries_lists/data/countries/pt/countries.json';

import useAPIMutation from '../../api/useAPIMutation';
import useAPIQuery from '../../api/useAPIQuery';
import useInfiniteAPIQuery from '../../api/useInfiniteAPIQuery';
import AddCandidateDrawer from '../../components/AddCandidateDrawer';
import ApplicantAnnotationModal from '../../components/ApplicantAnnotationModal';
import BulkSelectCandidateModal from '../../components/BulkSelectCandidateModal';
import BulkSendCustomTestsModal from '../../components/BulkSendCustomTestsModal';
import BulkSendEmailModal from '../../components/BulkSendEmailModal';
import BulkSendTestsModal from '../../components/BulkSendTestsModal';
import CandidateTable from '../../components/CandidateTable';
import ErrorResult from '../../components/ErrorResult';
import Filter from '../../components/Filter';
import { deleteScoreSearchParams } from '../../components/Filter/Utils/deleteScoreSearchParams';
import Page from '../../components/Page';
import PageContent from '../../components/PageContent';
import PageTitle from '../../components/PageTitle';
import PartialSelectCandidateModal from '../../components/PartialSelectCandidateModal';
import PartialSendCustomTestsModal from '../../components/PartialSendCustomTestsModal';
import PartialSendEmailModal from '../../components/PartialSendEmailModal';
import PartialSendTestsModal from '../../components/PartialSendTestsModal';
import RangeFilterTag from '../../components/RangeFilterTag';
import { SearchAttachmentContent } from '../../components/SearchEngine/SearchAttachmentContent';
import SearchParam from '../../components/SearchParam';
import usePrefetchPages from '../../hooks/usePrefetchPages';
import { SearchCandidate } from '../../hooks/useSearchCandidates';
import useSearchCandidatesController from '../../hooks/useSearchCandidatesController';
import useTypedNavigate from '../../hooks/useTypedNavigate';
import useTypedParams from '../../hooks/useTypedParams';
import { withRouteChange } from '../../services/APM';
import { CandidateCollection, CandidateId } from '../../types';
import CandidateVacancies from './CandidateVacancies';
import TalentPoolTableActions from './TalentPoolTableActions';

const gradeColumnOptions = [
  'TAGS',
  'MATCH',
  'RAW_POTENTIAL',
  'OCP',
  'LOGIC',
  'SJT',
  'MOTIVATIONAL',
  'OPERATIONAL',
];

const TalentPool = () => {
  const [searchParams, setSearchParams] = useSearchParams({
    sort: 'ocp',
    direction: 'descend',
    columns: ['TAGS', 'MATCH', 'RAW_POTENTIAL', 'OCP'],
  });

  const { tenant } = useTypedParams({ tenant: String });
  const navigate = useTypedNavigate();
  const queryClient = useQueryClient();

  useEffect(() => {
    setSearchParams(searchParams, { replace: true });
  }, [searchParams, setSearchParams]);

  const [sendEmailApplicants, setSendEmailApplicants] = useState<CandidateCollection['id'][]>([]);
  const [bulkSendCommunication, setBulkSendCommunication] = useState<boolean>(false);
  const [sendTestsApplicants, setSendTestsApplicants] = useState<CandidateCollection['id'][]>([]);
  const [bulkSendTests, setBulkSendTests] = useState<boolean>(false);
  const [sendCustomTestsApplicants, setSendCustomTestsApplicants] = useState<
    CandidateCollection['id'][]
  >([]);
  const [bulkSendCustomTests, setBulkSendCustomTests] = useState<boolean>(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [applicantAnnotation, setApplicantAnnotation] = useState<CandidateCollection['id']>();
  const [applicantSelection, setApplicantSelection] = useState<CandidateCollection['id'][]>([]);
  const [bulkSelect, setBulkSelect] = useState<boolean>(false);
  const [isAddCandidateOpen, setIsAddCandidateOpen] = useState(false);

  const searchCandidates = useSearchCandidatesController();

  const tableCandidateDataSource = searchCandidates.data;

  const config = useAPIQuery('/configs/tenant-configurations/');

  const functionPages = useInfiniteAPIQuery('/mindsight/ats-integrations/functions/');

  const functions = usePrefetchPages(functionPages, (data) => data.results);

  const candidateSourceOptionsPages = useInfiniteAPIQuery(['/candidate-source-options/']);

  const candidateSourceOptions = usePrefetchPages(
    candidateSourceOptionsPages,
    (page) => page.results
  );

  const [addressState, setAddressState] = useState('');
  const [addressCity, setAddressCity] = useState('');

  const candidateTagsPages = useInfiniteAPIQuery('/candidate-tags/');
  const candidateTags = usePrefetchPages(candidateTagsPages, (data) => data.results);

  const markAsUnviewedMutation = useAPIMutation(
    async ({ id }: { id: number | null }, api) => {
      await api.delete(`/candidate-user-visualizations/${id}/`);
    },
    {
      onSuccess: (_, __) => {
        queryClient.invalidateQueries('useSearchCandidates');
      },
    }
  );
  const isTableLoading =
    searchCandidates.isIdle || searchCandidates.isFetching || candidateTagsPages.isLoading;

  return (
    <Page>
      {isAddCandidateOpen && (
        <AddCandidateDrawer
          open={isAddCandidateOpen}
          onSuccess={(candidateId) => {
            message.success('Candidato adicionado no banco de talentos.');
            setIsAddCandidateOpen(false);
            navigate({
              pathname: '/:tenant/talent-pool/candidates/:applicant_id/details/',
              params: { tenant, applicant_id: candidateId },
              search: searchParams,
            });
          }}
          onClose={() => setIsAddCandidateOpen(false)}
        />
      )}
      <PageTitle
        title="Banco de Talentos"
        extra={
          <Button
            type="primary"
            icon={<UserAddOutlined />}
            onClick={() => setIsAddCandidateOpen(true)}
          >
            Adicionar candidato
          </Button>
        }
      />
      {applicantAnnotation && (
        <ApplicantAnnotationModal
          key={applicantAnnotation}
          fromStage={undefined}
          jobPostingId={undefined}
          open={!!applicantAnnotation}
          onCancel={() => setApplicantAnnotation(undefined)}
          candidateId={applicantAnnotation}
          onSuccess={() => {
            message.success('Comentário registrado.');
            setApplicantAnnotation(undefined);
          }}
          destroyOnClose
        />
      )}
      <BulkSendEmailModal
        open={bulkSendCommunication}
        totalCount={tableCandidateDataSource?.count}
        onCancel={() => setBulkSendCommunication(false)}
        onSuccess={() => {
          message.success('Mensagem enviada.');
          setBulkSendCommunication(false);
        }}
        destroyOnClose
      />
      <PartialSendEmailModal
        key={`sendEmailsModal-${sendEmailApplicants.join('')}`}
        candidateIds={sendEmailApplicants}
        open={sendEmailApplicants.length > 0}
        onCancel={() => setSendEmailApplicants([])}
        onSuccess={() => {
          message.success('Mensagem enviada.');
          setSelectedRowKeys([]);
          setSendEmailApplicants([]);
        }}
        destroyOnClose
      />
      <BulkSendTestsModal
        open={bulkSendTests}
        totalCount={tableCandidateDataSource?.count}
        onCancel={() => setBulkSendTests(false)}
        onSuccess={() => {
          message.success('Testes enviados.');
          setBulkSendTests(false);
        }}
        destroyOnClose
      />
      <PartialSendTestsModal
        key={`sendTestsModal-${sendEmailApplicants.join('')}`}
        candidateIds={sendTestsApplicants}
        open={sendTestsApplicants.length > 0}
        width={800}
        onSuccess={() => {
          message.success('Testes enviados.');
          setSelectedRowKeys([]);
          setSendTestsApplicants([]);
        }}
        onCancel={() => setSendTestsApplicants([])}
        destroyOnClose
      />
      {config.data?.enableAlchemerIntegration && (
        <>
          <BulkSendCustomTestsModal
            open={bulkSendCustomTests}
            width={800}
            totalCount={tableCandidateDataSource?.count}
            onSuccess={() => {
              message.success('Testes customizados enviados.', 6);
              setBulkSendCustomTests(false);
            }}
            onCancel={() => setBulkSendCustomTests(false)}
            destroyOnClose
          />
          <PartialSendCustomTestsModal
            key={`sendCustomTestsModal-${sendCustomTestsApplicants.join('')}`}
            candidateIds={sendCustomTestsApplicants}
            open={sendCustomTestsApplicants.length > 0}
            width={800}
            onSuccess={() => {
              message.success('Testes customizados enviados.', 6);
              setSelectedRowKeys([]);
              setSendCustomTestsApplicants([]);
            }}
            onCancel={() => setSendCustomTestsApplicants([])}
            destroyOnClose
          />
        </>
      )}
      <PartialSelectCandidateModal
        key={`SelectCandidateModal-${applicantSelection.join('')}`}
        ids={applicantSelection}
        actionOrigin="Banco de Talentos"
        open={applicantSelection.length > 0}
        onCancel={() => setApplicantSelection([])}
        onSuccess={() => {
          message.success('Candidato(s) triado(s).');
          setSelectedRowKeys([]);
          setApplicantSelection([]);
        }}
        destroyOnClose
      />
      <BulkSelectCandidateModal
        open={bulkSelect}
        actionOrigin="Banco de Talentos"
        onCancel={() => setBulkSelect(false)}
        totalCount={tableCandidateDataSource?.count}
        onSuccess={() => {
          message.success('Candidato(s) triado(s).');
          setBulkSelect(false);
        }}
        destroyOnClose
      />
      <ErrorResult error={searchCandidates.error}>
        <PageContent>
          <Outlet />
          <Card data-testid="main-card">
            <Filter
              onClear={() => {
                searchParams.set('page', '1');
                searchParams.delete('search');
                searchParams.delete('attachment_search');
                deleteScoreSearchParams(searchParams);
                searchParams.delete('search');
                searchParams.delete('addressContryIn');
                searchParams.delete('addressStateIn');
                searchParams.delete('addressCityIn');
                searchParams.delete('updatedAtMin');
                searchParams.delete('updatedAtMax');
                searchParams.delete('createdAtMin');
                searchParams.delete('createdAtMax');
                searchParams.delete('age_start');
                searchParams.delete('age_end');
                searchParams.delete('disabilities');
                searchParams.delete('candidateSourceOptions');
                searchParams.delete('tags');
                setSearchParams(searchParams);
              }}
            >
              <Col flex={1}>
                <SearchParam name="search" onSetSearchParams={{ page: '1' }}>
                  <Input.Search
                    allowClear
                    placeholder="Buscar candidatos"
                    data-testid="search-input"
                  />
                </SearchParam>
              </Col>
              {config.data?.enableAttachmentSearchForTalentPool && (
                <Col style={{ width: 450 }}>
                  <SearchAttachmentContent
                    isTableLoading={isTableLoading}
                    searchCandidateCount={searchCandidates.data?.count}
                    searchParams={searchParams}
                  />
                </Col>
              )}
              <Col span={4}>
                <SearchParam
                  name="match"
                  onSetSearchParams={(searchParam) => {
                    if (searchParam.get('match')) {
                      if (!searchParam.get('match_min') && !searchParam.get('match_max')) {
                        searchParam.set('match_min', '0');
                        searchParam.delete('match_max');
                      }

                      searchParam.set('sort', 'match');
                    } else {
                      searchParam.delete('match_min');
                      searchParam.delete('match_max');
                      searchParam.set('sort', 'ocp');
                    }

                    searchParam.set('direction', 'descend');
                    searchParam.set('page', '1');
                    setSearchParams(searchParam);
                  }}
                >
                  <Select
                    showSearch
                    placeholder="Selecione um arquétipo"
                    allowClear
                    style={{ width: '100%' }}
                  >
                    {functionPages.isLoading && searchParams.get('match') ? (
                      <Select.Option value={searchParams.get('match')}>...</Select.Option>
                    ) : (
                      functions.map((fn) => (
                        <Select.Option key={fn.id} value={`${fn.id}`}>
                          {fn.name}
                        </Select.Option>
                      ))
                    )}
                  </Select>
                </SearchParam>
              </Col>
              <Filter.Item
                label="Match"
                name="match"
                selected={
                  <RangeFilterTag
                    nameMin="match_min"
                    nameMax="match_max"
                    label="Match"
                    onSetSearchParams={{ page: '1' }}
                  />
                }
                group="Testes"
              >
                <Filter.Range nameMin="match_min" nameMax="match_max" />
              </Filter.Item>
              <Filter.Item
                label="Potencial Bruto"
                name="rawPotential"
                selected={
                  <RangeFilterTag
                    nameMin="raw_potential_min"
                    nameMax="raw_potential_max"
                    label="Potencial Bruto"
                    onSetSearchParams={{ page: '1' }}
                  />
                }
                group="Testes"
              >
                <Filter.Range nameMin="raw_potential_min" nameMax="raw_potential_max" />
              </Filter.Item>
              <Filter.Item
                label="Fit Cultural"
                name="ocp"
                selected={
                  <RangeFilterTag
                    nameMin="ocp_min"
                    nameMax="ocp_max"
                    label="Fit Cultural"
                    onSetSearchParams={{ page: '1' }}
                  />
                }
                group="Testes"
              >
                <Filter.Range nameMin="ocp_min" nameMax="ocp_max" />
              </Filter.Item>
              <Filter.Item
                label="Mental"
                name="logic"
                selected={
                  <RangeFilterTag
                    nameMin="logic_min"
                    nameMax="logic_max"
                    label="Raciocínio"
                    onSetSearchParams={{ page: '1' }}
                  />
                }
                group="Testes"
              >
                <Filter.Range nameMin="logic_min" nameMax="logic_max" />
              </Filter.Item>
              <Filter.Item
                label="Social"
                name="sjt"
                selected={
                  <RangeFilterTag
                    nameMin="sjt_min"
                    nameMax="sjt_max"
                    label="Social"
                    onSetSearchParams={{ page: '1' }}
                  />
                }
                group="Testes"
              >
                <Filter.Range nameMin="sjt_min" nameMax="sjt_max" />
              </Filter.Item>
              <Filter.Item
                label="Motivacional"
                name="motivational"
                selected={
                  <RangeFilterTag
                    nameMin="motivational_min"
                    nameMax="motivational_max"
                    label="Motivacional"
                    onSetSearchParams={{ page: '1' }}
                  />
                }
                group="Testes"
              >
                <Filter.Range nameMin="motivational_min" nameMax="motivational_max" />
              </Filter.Item>
              <Filter.Item
                label="Raciocínio Simplificado"
                name="operational_logic"
                selected={
                  <Filter.Selection name="operational_logic">
                    <Filter.Tag defaultValue="Aprovado">
                      Simplificado de raciocínio: Aprovado
                    </Filter.Tag>
                    <Filter.Tag defaultValue="Reprovado">
                      Simplificado de raciocínio: Reprovado
                    </Filter.Tag>
                  </Filter.Selection>
                }
                group="Testes"
              >
                <Filter.RadioGroup name="operational_logic">
                  <Radio value="Aprovado">Aprovado</Radio>
                  <Radio value="Reprovado">Reprovado</Radio>
                </Filter.RadioGroup>
              </Filter.Item>
              <Filter.Item
                name="disabilities"
                label="Pessoas com Deficiência"
                selected={
                  <Filter.Selection name="disabilities">
                    <Filter.Tag defaultValue="HEARING">Auditiva</Filter.Tag>
                    <Filter.Tag defaultValue="PHYSICAL">Física</Filter.Tag>
                    <Filter.Tag defaultValue="MENTAL">Mental</Filter.Tag>
                    <Filter.Tag defaultValue="VISUAL">Visual</Filter.Tag>
                    <Filter.Tag defaultValue="INTELLECTUAL">Intelectual</Filter.Tag>
                  </Filter.Selection>
                }
              >
                <SearchParam name="disabilities" array onSetSearchParams={{ page: '1' }}>
                  <Select mode="multiple" style={{ width: '100%' }} maxTagCount="responsive">
                    <Select.Option value="HEARING">Auditiva</Select.Option>
                    <Select.Option value="PHYSICAL">Física</Select.Option>
                    <Select.Option value="MENTAL">Mental</Select.Option>
                    <Select.Option value="VISUAL">Visual</Select.Option>
                    <Select.Option value="INTELLECTUAL">Intelectual</Select.Option>
                  </Select>
                </SearchParam>
              </Filter.Item>
              <Filter.Section label="Localidade">
                {[
                  <Filter.Item
                    name="addressContryIn"
                    key="country"
                    label="País"
                    hideSelected
                    selected={
                      <Filter.Selection name="addressContryIn" onSetSearchParams={{ page: '1' }}>
                        {(searchParams.getAll('addressContryIn') as string[]).map((countryName) => (
                          <Filter.Tag key={countryName} defaultValue={countryName}>
                            País:{' '}
                            {
                              countries.find(
                                (country) => country.alpha2.toLocaleUpperCase() === countryName
                              )?.name
                            }
                          </Filter.Tag>
                        ))}
                      </Filter.Selection>
                    }
                  >
                    <SearchParam name="addressContryIn" array onSetSearchParams={{ page: '1' }}>
                      <Select
                        allowClear
                        mode="multiple"
                        showSearch
                        filterOption={(input, option) =>
                          (option?.children as unknown as string)
                            .toLowerCase()
                            .includes(input.toLowerCase())
                        }
                        style={{ width: '100%' }}
                        data-testid="country-candidate-filter-select"
                      >
                        {countries.map((country) => (
                          <Select.Option
                            key={country.id}
                            value={country.alpha2.toLocaleUpperCase()}
                          >
                            {country.name}
                          </Select.Option>
                        ))}
                      </Select>
                    </SearchParam>
                  </Filter.Item>,
                  <Filter.Item
                    name="addressStateIn"
                    key="state"
                    label="Estado"
                    selected={
                      <Filter.Selection name="addressStateIn" onSetSearchParams={{ page: '1' }}>
                        {(searchParams.getAll('addressStateIn') as string[]).map((state) => (
                          <Filter.Tag key={state} defaultValue={state}>
                            Estado: {state}
                          </Filter.Tag>
                        ))}
                      </Filter.Selection>
                    }
                  >
                    <SearchParam name="addressStateIn" array onSetSearchParams={{ page: '1' }}>
                      {({ setSearchParam, value }) => (
                        <Input
                          data-testid="state-candidate-filter-input"
                          value={addressState}
                          onChange={(e) => setAddressState(e.target.value)}
                          onPressEnter={() => {
                            if (Array.isArray(value) && !value.includes(addressState)) {
                              setSearchParam([...value, addressState]);
                            }
                            setAddressState('');
                          }}
                        />
                      )}
                    </SearchParam>
                  </Filter.Item>,
                  <Filter.Item
                    name="addressCityIn"
                    label="Cidade"
                    key="city"
                    selected={
                      <Filter.Selection name="addressCityIn" onSetSearchParams={{ page: '1' }}>
                        {(searchParams.getAll('addressCityIn') as string[]).map((city) => (
                          <Filter.Tag key={city} defaultValue={city}>
                            Cidade: {city}
                          </Filter.Tag>
                        ))}
                      </Filter.Selection>
                    }
                  >
                    <SearchParam name="addressCityIn" array onSetSearchParams={{ page: '1' }}>
                      {({ setSearchParam, value }) => (
                        <Input
                          data-testid="city-candidate-filter-input"
                          value={addressCity}
                          onChange={(e) => setAddressCity(e.target.value)}
                          onPressEnter={() => {
                            if (Array.isArray(value) && !(addressCity in value)) {
                              setSearchParam([...value, addressCity]);
                            }
                            setAddressCity('');
                          }}
                        />
                      )}
                    </SearchParam>
                  </Filter.Item>,
                ]}
              </Filter.Section>
              <Filter.Item
                name="updated_at"
                label="Data de atualização do currículo"
                selected={
                  <RangeFilterTag
                    nameMin="updatedAtMin"
                    nameMax="updatedAtMax"
                    label="Data de atualização do currículo"
                    onSetSearchParams={{ page: '1' }}
                  />
                }
              >
                <Filter.DateRange start="updatedAtMin" end="updatedAtMax" />
              </Filter.Item>
              <Filter.Item
                name="created_at"
                label="Data de criação do perfil"
                selected={
                  <RangeFilterTag
                    nameMin="createdAtMin"
                    nameMax="createdAtMax"
                    label="Data de criação do perfil"
                    onSetSearchParams={{ page: '1' }}
                  />
                }
              >
                <Filter.DateRange start="createdAtMin" end="createdAtMax" />
              </Filter.Item>
              <Col>
                <Popover
                  placement="bottomRight"
                  content={
                    <Space direction="vertical">
                      <SearchParam name="columns">
                        <Checkbox.Group>
                          <Space direction="vertical">
                            {gradeColumnOptions.map((column) => {
                              switch (column) {
                                case 'TAGS':
                                  return (
                                    <Checkbox key={column} name="columns" value={column}>
                                      Tags
                                    </Checkbox>
                                  );
                                case 'LOGIC':
                                  return (
                                    <Checkbox key={column} name="columns" value={column}>
                                      Mental
                                    </Checkbox>
                                  );
                                case 'SJT':
                                  return (
                                    <Checkbox key={column} name="columns" value={column}>
                                      Social
                                    </Checkbox>
                                  );
                                case 'MOTIVATIONAL':
                                  return (
                                    <Checkbox key={column} name="columns" value={column}>
                                      Motivacional
                                    </Checkbox>
                                  );
                                case 'OPERATIONAL':
                                  return (
                                    <Checkbox key={column} name="columns" value={column}>
                                      Raciocínio Simplificado
                                    </Checkbox>
                                  );
                                case 'MATCH':
                                  return (
                                    <Checkbox key={column} name="columns" value={column}>
                                      Match
                                    </Checkbox>
                                  );
                                case 'RAW_POTENTIAL':
                                  return (
                                    <Checkbox key={column} name="columns" value={column}>
                                      Potencial Bruto
                                    </Checkbox>
                                  );
                                case 'OCP':
                                  return (
                                    <Checkbox key={column} name="columns" value={column}>
                                      Fit Cultural
                                    </Checkbox>
                                  );
                              }
                            })}
                          </Space>
                        </Checkbox.Group>
                      </SearchParam>
                      <Space>
                        <SearchParam array name="columns" valuePropName="disabledValue">
                          <Filter.Button value={gradeColumnOptions}>Todas</Filter.Button>
                        </SearchParam>
                        <SearchParam array name="columns" valuePropName="disabledValue">
                          <Filter.Button>Limpar</Filter.Button>
                        </SearchParam>
                      </Space>
                    </Space>
                  }
                >
                  <Button>
                    <TableOutlined />
                  </Button>
                </Popover>
              </Col>
              <Filter.Item
                name="birthday"
                label="Idade"
                selected={
                  <RangeFilterTag
                    nameMin="age_start"
                    nameMax="age_end"
                    label="Idade"
                    onSetSearchParams={{ page: '1' }}
                  />
                }
              >
                <Filter.Range nameMin="age_start" nameMax="age_end" />
              </Filter.Item>
              <Filter.Item
                name="candidateSourceOptions"
                label="Origem do Candidato"
                selected={
                  <Filter.Selection name="candidateSourceOptions" onSetSearchParams={{ page: '1' }}>
                    {candidateSourceOptions.map((candidateSourceOption) => (
                      <Filter.Tag
                        key={candidateSourceOption.name}
                        defaultValue={candidateSourceOption.name}
                      >
                        {candidateSourceOption.name}
                      </Filter.Tag>
                    ))}
                  </Filter.Selection>
                }
              >
                <SearchParam name="candidateSourceOptions" array onSetSearchParams={{ page: '1' }}>
                  <Select
                    data-testid="filter-candidate-source-options-input"
                    mode="multiple"
                    style={{ width: '100%' }}
                    maxTagCount="responsive"
                    options={candidateSourceOptions.map((candidateSourceOption) => ({
                      value: candidateSourceOption.name,
                      label: candidateSourceOption.name,
                    }))}
                  />
                </SearchParam>
              </Filter.Item>
              <Filter.Item
                name="tags"
                label="Tags"
                selected={
                  <Filter.Selection name="tags">
                    {candidateTags.map(({ id, name }) => (
                      <Filter.Tag key={id} defaultValue={`${id}`}>
                        {name}
                      </Filter.Tag>
                    ))}
                  </Filter.Selection>
                }
              >
                <SearchParam name="tags" array onSetSearchParams={{ page: '1' }}>
                  <Select
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    style={{ width: '100%' }}
                    mode="multiple"
                    maxTagCount="responsive"
                    options={candidateTags.map(({ id, name }) => ({
                      key: id.toString(),
                      label: name,
                      value: id.toString(),
                    }))}
                  />
                </SearchParam>
              </Filter.Item>
            </Filter>
            {selectedRowKeys.length > 0 && (
              <Alert
                key="selectedCandidatesAlert"
                message={`Você selecionou ${selectedRowKeys.length} candidato(s)`}
                action={
                  <Space>
                    <Button onClick={() => setApplicantSelection(selectedRowKeys.map(CandidateId))}>
                      Triar para vaga
                    </Button>
                    <Dropdown
                      menu={{
                        onClick: ({ key }) => {
                          switch (key) {
                            case 'email':
                              setSendEmailApplicants(selectedRowKeys.map(CandidateId));
                              break;
                            case 'tests':
                              setSendTestsApplicants(selectedRowKeys.map(CandidateId));
                              break;
                            case 'custom-tests':
                              setSendCustomTestsApplicants(selectedRowKeys.map(CandidateId));
                              break;
                          }
                        },
                        items: [
                          { key: 'email', label: 'Enviar comunicação' },
                          { key: 'tests', label: 'Enviar testes' },
                          ...(config.data?.enableAlchemerIntegration
                            ? [{ key: 'custom-tests', label: 'Enviar testes customizados' }]
                            : []),
                        ],
                      }}
                    >
                      <Button>
                        Enviar e-mail <DownOutlined />
                      </Button>
                    </Dropdown>
                    <Button type="link" onClick={() => setSelectedRowKeys([])}>
                      Limpar
                    </Button>
                  </Space>
                }
              />
            )}
            <CandidateTable
              dataSource={tableCandidateDataSource?.results}
              pagination={{
                total: tableCandidateDataSource?.count,
              }}
              loading={isTableLoading}
              onRowClick={(recordId) => {
                const newSearchParams = createSearchParams(searchParams);
                if (searchParams.get('match')) {
                  newSearchParams.set('functionCompareId', searchParams.get('match') || '');

                  return {
                    pathname: `./candidates/${recordId}/tests`,
                    search: createSearchParams(newSearchParams).toString(),
                  };
                }
                return {
                  pathname: `./candidates/${recordId}/details`,
                  search: newSearchParams.toString(),
                };
              }}
              rowSelection={{
                type: 'checkbox',
                selectedRowKeys,
                preserveSelectedRowKeys: true,
                onChange: (selectedRowKeys) => setSelectedRowKeys(selectedRowKeys),
                selections: [
                  {
                    key: 'bulk-select',
                    text: 'Triar todos os candidatos',
                    onSelect: () => setBulkSelect(true),
                  },
                  {
                    key: 'bulk-send-communication',
                    text: 'Enviar comunicação para todos os candidatos',
                    onSelect: () => setBulkSendCommunication(true),
                  },
                  {
                    key: 'bulk-send-tests',
                    text: 'Enviar testes para todos os candidatos',
                    onSelect: () => setBulkSendTests(true),
                  },
                  ...(config.data?.enableAlchemerIntegration
                    ? [
                        {
                          key: 'bulk-send-custom-tests',
                          text: 'Enviar testes customizados para todos os candidatos',
                          onSelect: () => {
                            setBulkSendCustomTests(true);
                          },
                        },
                      ]
                    : []),
                ],
              }}
              expandable={{
                expandedRowRender: (record) => {
                  return <CandidateVacancies candidateId={record.id} />;
                },
              }}
              candidateTags={candidateTags}
            >
              <Table.Column
                title="Ações"
                render={(_, record: SearchCandidate) => {
                  return (
                    <TalentPoolTableActions
                      candidateId={record.id}
                      candidateName={record.name}
                      hasViewed={Boolean(record.recruiter_visualization_id)}
                      enableAlchemerIntegration={config.data?.enableAlchemerIntegration}
                      onSelectClick={() => setApplicantSelection([record.id])}
                      onSendAnnotationClick={() => setApplicantAnnotation(record.id)}
                      onSendEmailClick={() => setSendEmailApplicants([record.id])}
                      onSendTestsClick={() => setSendTestsApplicants([record.id])}
                      onSendCustomTestsClick={() => setSendCustomTestsApplicants([record.id])}
                      onMarkAsUnviewedClick={() =>
                        markAsUnviewedMutation.mutate({ id: record.recruiter_visualization_id })
                      }
                    />
                  );
                }}
                width={1}
                align="center"
              />
            </CandidateTable>
          </Card>
        </PageContent>
      </ErrorResult>
    </Page>
  );
};

export default withRouteChange()(TalentPool);
